/**
 * Public marketing layout — header, footer and a skip link.
 */

import { useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { config } from '../config/appConfig.js';
import { useAuth } from '../providers/AuthProvider.jsx';
import { Logo } from '../components/brand/Logo.jsx';
import { Button } from '../components/ui/Button.jsx';
import { ThemeToggle } from '../components/ui/ThemeToggle.jsx';
import styles from './MarketingLayout.module.css';

const NAV_LINKS = [
  { to: '/features', label: 'Features' },
  { to: '/guitar', label: 'Guitar' },
  { to: '/piano', label: 'Piano' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/faq', label: 'FAQ' },
];

export function MarketingLayout() {
  const { isAuthenticated } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close the mobile menu whenever the route changes.
  const currentPath = location.pathname;
  const [lastPath, setLastPath] = useState(currentPath);
  if (currentPath !== lastPath) {
    setLastPath(currentPath);
    if (menuOpen) setMenuOpen(false);
  }

  return (
    <div className={styles.shell}>
      <a href="#main" className="skip-link">
        Skip to content
      </a>

      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to="/" className={styles.logoLink} aria-label={`${config.appName} home`}>
            <Logo />
          </Link>

          <nav className={styles.desktopNav} aria-label="Main">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ''}`}
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className={styles.headerActions}>
            <ThemeToggle />

            {isAuthenticated ? (
              <Button to="/app" variant="primary" size="sm">
                Open {config.appName}
              </Button>
            ) : (
              <>
                <Button to="/login" variant="ghost" size="sm" className={styles.hideOnMobile}>
                  Sign in
                </Button>
                <Button to="/register" variant="primary" size="sm">
                  Get started free
                </Button>
              </>
            )}

            <button
              type="button"
              className={styles.menuButton}
              onClick={() => setMenuOpen((open) => !open)}
              aria-expanded={menuOpen}
              aria-controls="marketing-mobile-nav"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-hidden="true">
                {menuOpen ? (
                  <path d="M5 5l12 12M17 5L5 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                ) : (
                  <path d="M3 6h16M3 11h16M3 16h16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <nav id="marketing-mobile-nav" className={styles.mobileNav} aria-label="Main">
            {NAV_LINKS.map((link) => (
              <NavLink key={link.to} to={link.to} className={styles.mobileNavLink}>
                {link.label}
              </NavLink>
            ))}
            {!isAuthenticated && (
              <NavLink to="/login" className={styles.mobileNavLink}>
                Sign in
              </NavLink>
            )}
          </nav>
        )}
      </header>

      <main id="main" className={styles.main}>
        <Outlet />
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div className={styles.footerBrand}>
            <Logo />
            <p className={styles.tagline}>{config.appTagline}</p>
          </div>

          <div className={styles.footerColumns}>
            <div className={styles.footerColumn}>
              <h2 className={styles.footerHeading}>Product</h2>
              <Link to="/features">Features</Link>
              <Link to="/guitar">For guitarists</Link>
              <Link to="/piano">For pianists</Link>
              <Link to="/pricing">Pricing</Link>
              <Link to="/download">Download</Link>
            </div>

            <div className={styles.footerColumn}>
              <h2 className={styles.footerHeading}>Support</h2>
              <Link to="/faq">FAQ</Link>
              <Link to="/contact">Contact</Link>
              <Link to="/account-deletion">Delete your account</Link>
            </div>

            <div className={styles.footerColumn}>
              <h2 className={styles.footerHeading}>Legal</h2>
              <Link to="/privacy">Privacy</Link>
              <Link to="/terms">Terms</Link>
              <Link to="/copyright">Copyright</Link>
            </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <p>
            © {new Date().getFullYear()} {config.companyName}. All rights reserved.
          </p>
          <p className={styles.footerNote}>
            {config.appName} is an independent product. It is not affiliated with, endorsed by, or
            connected to any other chord or tab service.
          </p>
        </div>
      </footer>
    </div>
  );
}
